import {API_BASE_URL} from '../utils/constants';

// Safely converts any FastAPI error detail value to a readable string.
// Handles: string, Pydantic v2 validation array [{loc, msg}], plain object, null/undefined.
const _detailToString = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        try { return detail.map(e => `${(e.loc || []).join('.')}: ${e.msg}`).join('; '); }
        catch (_) { return JSON.stringify(detail); }
    }
    try { return JSON.stringify(detail); } catch (_) { return fallback; }
};

// API service for Middle Stage (student)
const middleApiService = {
    // Get token from localStorage
    getToken: () => {
        const token = localStorage.getItem("accessToken");
        if (!token) {
            console.error("No access token found in localStorage");
        }
        return token;
    },

    // Get assignment details (student-specific, returns StudentDomainAssignment)
    getAssignment: async (assignmentId) => {
        try {
            const accessToken = middleApiService.getToken();
            if (!accessToken) {
                throw new Error("Authentication required");
            }

            const response = await fetch(
                `${API_BASE_URL}/middle/assignments/${assignmentId}`,
                {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (!response.ok) {
                let errorDetail = "Failed to fetch assignment details";
                try {
                    const errorData = await response.json();
                    errorDetail = _detailToString(errorData.detail, errorDetail);
                } catch (e) {
                    errorDetail = response.statusText || errorDetail;
                }
                throw new Error(errorDetail);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching middle stage assignment:", error);
            throw error;
        }
    },

    // List all domain assignments assigned to *me* in a class
    getClassAssignments: async (classId) => {
        try {
            const accessToken = middleApiService.getToken();
            if (!accessToken) {
                throw new Error("Authentication required");
            }

            const response = await fetch(
                `${API_BASE_URL}/middle/user/classes/${classId}/assignments`,
                {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (!response.ok) {
                let errorDetail = `Failed to fetch middle stage class assignments (Status: ${response.status})`;
                try {
                    const errorData = await response.json();
                    errorDetail = _detailToString(errorData.detail, errorDetail);
                } catch (e) {
                    errorDetail = response.statusText || errorDetail;
                }
                throw new Error(errorDetail);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching middle stage class assignments:", error);
            throw error;
        }
    },

    // Get submission for assignment (SubmissionDetail)
    getSubmission: async (assignmentId) => {
        try {
            const accessToken = middleApiService.getToken();
            if (!accessToken) {
                throw new Error("Authentication required");
            }

            const response = await fetch(
                `${API_BASE_URL}/middle/assignments/${assignmentId}/submission`,
                {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (!response.ok) {
                // 404 = no submission
                if (response.status === 404) {
                    throw new Error("No submission found");
                }
                let errorDetail = "Failed to fetch middle stage submission";
                try {
                    const errorData = await response.json();
                    errorDetail = _detailToString(errorData.detail, errorDetail);
                } catch (e) {
                    errorDetail = response.statusText || errorDetail;
                }
                throw new Error(errorDetail);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching middle stage submission:", error);
            throw error;
        }
    },

    // Add self-assessment (SelfAssessmentPackage model, returns updated SubmissionDetail)
    addSelfAssessment: async (assignmentId, selfAssessmentData) => {
        try {
            const accessToken = middleApiService.getToken();
            if (!accessToken) {
                throw new Error("Authentication required");
            }

            const response = await fetch(
                `${API_BASE_URL}/middle/assignments/${assignmentId}/self-assessment`,
                {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(selfAssessmentData)
                }
            );

            if (!response.ok) {
                let errorDetail = "Failed to add middle stage self-assessment";
                try {
                    const errorData = await response.json();
                    console.error("Self assessment error details:", errorData);
                    errorDetail = _detailToString(errorData.detail, errorDetail);
                } catch (e) {
                    errorDetail = response.statusText || errorDetail;
                }
                throw new Error(errorDetail);
            }

            return await response.json();
        } catch (error) {
            console.error("Error adding middle stage self-assessment:", error);
            throw error;
        }
    },

    // Add peer-assessment (PeerReflectionPackage model, returns updated SubmissionDetail)
    addPeerAssessment: async (assignmentId, peerAssessmentData) => {
        try {
            const accessToken = middleApiService.getToken();
            if (!accessToken) {
                throw new Error("Authentication required");
            }

            const response = await fetch(
                `${API_BASE_URL}/middle/assignments/${assignmentId}/peer-assessment`,
                {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(peerAssessmentData)
                }
            );

            if (!response.ok) {
                let errorDetail = "Failed to add middle stage peer-assessment";
                try {
                    const errorData = await response.json();
                    console.error("Peer assessment error details:", errorData);
                    errorDetail = _detailToString(errorData.detail, errorDetail);
                } catch (e) {
                    errorDetail = response.statusText || errorDetail;
                }
                throw new Error(errorDetail);
            }

            return await response.json();
        } catch (error) {
            console.error("Error adding middle stage peer-assessment:", error);
            throw error;
        }
    },

    // Get the student's HPC report (Holistic Progress Card)
    getMyHPCReport: async () => {
        try {
            const accessToken = middleApiService.getToken();
            if (!accessToken) {
                throw new Error("Authentication required");
            }

            const response = await fetch(
                `${API_BASE_URL}/middle/user/student/hpc-report`,
                {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (!response.ok) {
                let errorDetail = "Failed to fetch HPC report";
                try {
                    const errorData = await response.json();
                    errorDetail = _detailToString(errorData.detail, errorDetail);
                } catch (e) {
                    errorDetail = response.statusText || errorDetail;
                }
                throw new Error(errorDetail);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching HPC report:", error);
            throw error;
        }
    },

    // Submit assignment (unified method for both file and text submissions)
    submitAssignment: async (assignmentId, dataOrFile, isTextSubmission = false) => {
        const accessToken = middleApiService.getToken();
        if (!accessToken) {
            throw new Error("Authentication required");
        }

        try {
            const formData = new FormData();

            if (isTextSubmission) {
                if (!Array.isArray(dataOrFile) || dataOrFile.length === 0) {
                    throw new Error("Text submission requires a non-empty array of answers");
                }

                // FastAPI aggregates repeated keys into List[str]
                dataOrFile.forEach(ans => formData.append("answers", ans));
                console.log("[middle submitAssignment] sending TEXT answers", dataOrFile.length);
            } else {
                if (!(dataOrFile instanceof File)) {
                    throw new Error("File submission expects a File object");
                }

                formData.append("file", dataOrFile);
                console.log("[middle submitAssignment] sending FILE", {
                    name: dataOrFile.name,
                    size: dataOrFile.size,
                    type: dataOrFile.type,
                });
            }

            const response = await fetch(
                `${API_BASE_URL}/middle/assignments/${assignmentId}/submit`,
                {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                        // NO manual Content-Type header – the browser sets the boundary
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                let errorDetail = response.statusText || "Submission failed";
                try {
                    const errorData = await response.json();
                    if (errorData.detail) errorDetail = _detailToString(errorData.detail, errorDetail);
                } catch (e) {
                    // ignore JSON parse errors
                }
                throw new Error(errorDetail);
            }

            return await response.json();
        } catch (error) {
            console.error("[middle submitAssignment] error:", error);
            throw error;
        }
    }
};

export default middleApiService;